<script setup>
import { computed } from 'vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import { timeAgo } from '@/utils/timeAgo';

// One notification row: category icon (or the item's own), title, optional
// badge, short text, optional recommendation, time and an unread dot. The
// whole row opens it.
// `compact` clamps long text (the bell's popover).
const props = defineProps({
  item: { type: Object, required: true },
  compact: { type: Boolean, default: false },
});
defineEmits(['open']);

const ICONS = {
  lardo: 'mdi-file-document-alert-outline',
  risk: 'mdi-alert-circle-outline',
  parent: 'mdi-account-child-outline',
};
const LABELS = { lardo: 'Early warning', risk: 'At-risk' };

const ago = computed(() => timeAgo(props.item.createdAt));
const label = computed(() => LABELS[props.item.category] || '');
</script>

<template>
  <button
    type="button"
    class="item"
    :class="[`item--${item.category}`, { 'item--unread': !item.read, 'item--compact': compact }]"
    @click="$emit('open', item)"
  >
    <span class="item__icon" aria-hidden="true">
      <v-icon size="18">{{ item.icon || ICONS[item.category] || 'mdi-bell-outline' }}</v-icon>
    </span>

    <span class="item__body">
      <span class="item__top">
        <span class="item__title">{{ item.title }}</span>
        <UiBadge v-if="item.badge" :variant="item.badgeVariant || 'amber'">
          {{ item.badge }}
        </UiBadge>
        <span v-if="ago" class="item__time">{{ ago }}</span>
      </span>
      <span v-if="label || item.meta" class="item__meta">
        {{ [label, item.meta].filter(Boolean).join(' · ') }}
      </span>
      <span v-if="item.text" class="item__text">{{ item.text }}</span>
      <span v-if="item.recommendation" class="item__rec">
        <span class="item__rec-label">Recommendation</span>
        {{ item.recommendation }}
      </span>
    </span>

    <span v-if="!item.read" class="item__dot" aria-label="Unread" />
  </button>
</template>

<style scoped>
.item {
  --tone: var(--muted-foreground);
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 10px 28px 10px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.item:hover {
  background: var(--accent);
}
.item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
.item--updates { --tone: rgb(var(--brand-rgb)); }
.item--lardo { --tone: #6366f1; }
.item--risk { --tone: #f97316; }
.item--parent { --tone: #0d9488; }

.item__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--tone) 12%, transparent);
  color: var(--tone);
}

.item__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.item__top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.item__title {
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item--unread .item__title {
  font-weight: 600;
}
.item__time {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 12px;
  color: var(--faint-foreground);
}
.item__meta {
  font-size: 12px;
  color: var(--muted-foreground);
}
.item__text {
  font-size: 13px;
  line-height: 1.45;
  color: var(--secondary-foreground);
}
.item__rec {
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--subtle);
  border: 1px solid var(--border);
  font-size: 12px;
  line-height: 1.45;
  color: var(--secondary-foreground);
}
.item__rec-label {
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
  color: var(--foreground);
}
.item--compact .item__text,
.item--compact .item__rec {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.item--compact .item__rec {
  -webkit-line-clamp: 3;
}

.item__dot {
  position: absolute;
  top: 16px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--brand);
}
</style>
